import { Box, type SxProps, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import type { UseHppdByDimensionResult } from "../../../hooks/useHppdByDimension";
import { hppdTargetForDate } from "../../../utils/models";
import { formatNumber } from "../../../utils/utilFunctions";
import { HppdKeyTypography } from "./HppdKeyTypography";

type Props = {
	hppdBreakdownResult: UseHppdByDimensionResult;
	date: DateTime;
	sxOverride?: SxProps;
};

export const HppdBreakdownTypography: FC<Props> = ({ hppdBreakdownResult, date, sxOverride }) => {
	const { chosenDepartment } = useChosenDepartment();
	const dailyTarget = hppdTargetForDate(chosenDepartment.hppd_target, date.weekday);

	return (
		<Box sx={{ display: "inline-grid", gridTemplateColumns: "auto auto", columnGap: 1, alignItems: "center" }}>
			{hppdBreakdownResult.totalBreakdown.map(({ key, hppd }) => (
				<Box key={key} sx={{ display: "contents" }}>
					<HppdKeyTypography keyString={key} sxOverride={sxOverride} />
					<Typography sx={{ fontWeight: 600, color: "primary.main", justifySelf: "end", ...sxOverride }}>
						{formatNumber(hppd)}
						{!hppdBreakdownResult.isSeparated && `/${dailyTarget}`}
					</Typography>
				</Box>
			))}
			<Typography
				sx={{
					fontWeight: 600,
					color: "primary.main",
					borderTop: hppdBreakdownResult.totalBreakdown.length ? "1px solid #E0E0E0" : undefined,
					...sxOverride,
				}}
			>
				Total
			</Typography>
			<Typography
				data-testid="day-schedule-hppd-total"
				sx={{
					fontWeight: 600,
					color: "primary.main",
					justifySelf: "end",
					borderTop: hppdBreakdownResult.totalBreakdown.length ? "1px solid #E0E0E0" : undefined,
					...sxOverride,
				}}
			>
				{formatNumber(hppdBreakdownResult.totalHppd)}/{formatNumber(dailyTarget)}
			</Typography>
		</Box>
	);
};
